import DOMPurify from 'dompurify';

DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
  const { attrName, attrValue } = data;
  if (attrName === 'style' && /url\s*\(|expression|javascript:|@import|behavior|-moz-binding/i.test(attrValue)) data.keepAttr = false;
  if ((attrName === 'src' || attrName === 'href') && /^\s*data:/i.test(attrValue)) data.keepAttr = false;
  if (attrName === 'title' && _node.nodeName !== 'IMG') data.keepAttr = false;
  if (attrName === 'aria-label' && _node.nodeName !== 'TABLE') data.keepAttr = false;
});

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.nodeName === 'A') node.setAttribute('rel', 'noopener noreferrer');
});

const HTML_ELEMENTS = new Set(
  (
    'a abbr acronym address applet area article aside audio b base basefont bdi bdo bgsound big blink blockquote body br button ' +
    'canvas caption center cite code col colgroup command content data datalist dd del details dfn dialog dir div dl dt element em ' +
    'embed fieldset figcaption figure font footer form frame frameset h1 h2 h3 h4 h5 h6 head header hgroup hr html i iframe image img ' +
    'input ins isindex kbd keygen label legend li link listing main map mark marquee math menu menuitem meta meter multicol nav nextid ' +
    'nobr noembed noframes noscript object ol optgroup option output p param picture plaintext pre progress q rb rp rt rtc ruby s samp ' +
    'script search section select shadow slot small source spacer span strike strong style sub summary sup svg table tbody td template ' +
    'textarea tfoot th thead time title tr track tt u ul var video wbr xmp'
  ).split(' '),
);

const ANGLE_RE = /<([a-zA-Z][a-zA-Z0-9:-]*)(?=[\s/>]|$)[^<>]*(?:>|(?=<)|$)/g;

const escapeText = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const maskNonHtml = (input: string) => {
  const nonce = Math.random().toString(36).slice(2, 10);
  const kept: string[] = [];
  const text = input.replace(ANGLE_RE, (match: string, name: string, offset: number) => {
    if (HTML_ELEMENTS.has(name.toLowerCase())) return match;
    if (new RegExp(`</\\s*${name}\\s*>`, 'i').test(input.slice(offset + match.length))) return match;
    return `${nonce}:${kept.push(match) - 1}`;
  });
  const re = new RegExp(`${nonce}:(\\d+)`, 'g');
  return { text, restore: (out: string) => out.replace(re, (_m, n: string) => escapeText(kept[Number(n)] ?? '')) };
};

const warnedSinks = new Set<string>();

export const resetSafeHtmlWarnings = () => warnedSinks.clear();

export const safeHtml = (html: string | null | undefined, sink: string): string => {
  if (!html) return '';
  const raw = String(html);
  if (!/<[a-zA-Z!/?]/.test(raw)) return raw;
  const { text: input, restore } = maskNonHtml(raw);
  if (!/<[a-zA-Z!/?]/.test(input)) return restore(input);
  if (!DOMPurify.isSupported) return '';

  const clean = DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [
      'a', 'b', 'strong', 'i', 'em', 'u', 'br', 'p', 'span', 'div', 'ul', 'ol', 'li',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'img', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'hr',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'title', 'aria-label', 'width', 'height', 'class', 'style'],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  });

  const removed = (DOMPurify.removed as Array<{ element?: Node; attribute?: Attr | null }>).filter(
    (item) => item.attribute || !['BODY', 'HEAD', 'HTML'].includes(item.element?.nodeName || ''),
  );
  if (removed.length > 0 && !warnedSinks.has(sink)) {
    warnedSinks.add(sink);
    const summary = Array.from(
      new Set(removed.map((item) => (item.attribute ? `@${item.attribute.name}` : `<${(item.element?.nodeName || '?').toLowerCase()}>`))),
    ).join(', ');
    console.warn('[SANITIZE]', sink, summary);
  }
  return restore(clean);
};
